//third-party
import { useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useLoaderData } from "react-router-dom";
import { CarFront, ExclamationTriangleFill, InfoCircleFill } from "react-bootstrap-icons";
import { useAtom } from "jotai";
import { toast } from "react-toastify";

//spotsync connect
import { Api, VehicleData } from "./Data/Api";
import { Button          } from "./Controls/Button";
import { AddVehicleModal } from "./Controls/AddVehicleModal";
import { AddVehicleInline } from "./Controls/AddVehicleInline";
import { VehicleCard     } from "./Controls/VehicleCard";
import { ChooseVehicleModal } from "./Controls/ChooseVehicleModal";
import { ChooseSubscriptionModal } from "./Controls/ChooseSubscriptionModal";
import { PageTitle       } from "./Controls/PageTitle";
import { ConfirmModal    } from "./Controls/ConfirmModal";
import { processResult   } from "./Data/Result";
import { SubListEntry, Vehicle, VehicleSubscriptionSummary } from "./Data/ApiTypes";
import { SubscriptionStatus } from "./Data/ApiTransport";
import { parseSubListEntry, parseVehicle } from "./Data/ApiParse";
import { authAtom } from "./Data/Atoms";

export interface VehiclesPageLoaderData {
    vehicles:      Vehicle[];
    subscriptions: SubListEntry[];
}

export function UserVehiclesPage() {
    const loaded = useLoaderData() as VehiclesPageLoaderData;
    const [vehicles, setVehicles] = useState<Vehicle[]>( loaded.vehicles );
    const [subscriptions, setSubscriptions] = useState<SubListEntry[]>( loaded.subscriptions );
    const [showAddVehicle, setShowAddVehicle] = useState( false );
    const [showFirstVehicleForm, setShowFirstVehicleForm] = useState( true );
    const [assignSub, setAssignSub] = useState<SubListEntry | null>( null );
    const [assignVehicle, setAssignVehicle] = useState<Vehicle | null>( null );
    const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle | null>( null );
    const [unlinkTarget, setUnlinkTarget] = useState<{ vehicle: Vehicle; subscription: VehicleSubscriptionSummary } | null>( null );
    const [auth] = useAtom( authAtom );

    const myParkerId = auth.account?.parkerId ?? 0;
    const needing = plansNeedingVehicle( subscriptions, vehicles, myParkerId );
    const assignedIds = vehicles
        .filter( v => (v.subscriptions?.length ?? 0) > 0 )
        .map( v => v.vehicleId );

    function reload() {
        return Promise.all( [Api.vehicleList(), Api.subscriptionList()] ).then( ([vRes, sRes]) => {
            processResult( vRes, val => setVehicles( val.map( parseVehicle ) ) );
            processResult( sRes, val => setSubscriptions( val.map( parseSubListEntry ) ) );
        } );
    }

    function addVehicle( vehicle: VehicleData, subscriptionId?: number ) {
        const subId = subscriptionId ?? assignSub?.subscriptionId;
        Api.vehicleAdd( vehicle ).then( res => processResult( res, created => {
            if( subId == null ) {
                reload();
                return;
            }
            Api.subscriptionVehicleAdd( subId, { vehicleId: created.vehicleId } ).then( linkRes => processResult( linkRes,
                () => {
                    toast.success( "Vehicle assigned" );
                    setAssignSub( null );
                    reload();
                },
                err => {
                    toast.error( err );
                    reload();
                }
            ) );
        }, err => toast.error( err ) ) );
    }

    function deleteVehicle( vehicle: Vehicle ) {
        setVehicleToDelete( vehicle );
    }

    function confirmDelete() {
        if( vehicleToDelete == null ) return;
        Api.vehicleDelete( vehicleToDelete.vehicleId ).then( res => processResult( res, () => { reload(); } ) );
    }

    function confirmUnlink() {
        if( unlinkTarget == null ) return;
        const { vehicle, subscription } = unlinkTarget;
        Api.subscriptionVehicleDelete( subscription.subscriptionId, vehicle.vehicleId, { vehicleId: vehicle.vehicleId } )
            .then( res => processResult( res,
                () => {
                    toast.success( "Vehicle unlinked" );
                    reload();
                },
                err => toast.error( err )
            ) );
    }

    function linkVehicle( subscriptionId: number, vehicleId: number ) {
        Api.subscriptionVehicleAdd( subscriptionId, { vehicleId } ).then( res => processResult( res,
            () => {
                toast.success( "Vehicle assigned" );
                reload();
            },
            err => toast.error( err )
        ) );
    }

    const needCount = needing.length;
    const needHeading = needCount === 1
        ? "1 subscription needs a vehicle"
        : `${needCount} subscriptions need a vehicle`;
    const needDetail = needCount === 1
        ? "Assign a vehicle so your plate is recognized when you park."
        : "Assign a vehicle to each one so your plate is recognized when you park.";

    return <Container fluid>
        <Row>
            <Col>
                <AddVehicleModal
                    show={showAddVehicle}
                    setShow={setShowAddVehicle}
                    onSubmit={addVehicle}
                />
                <ChooseVehicleModal
                    show={assignSub !== null}
                    setShow={ open => { if( !open ) setAssignSub( null ); } }
                    vehicles={vehicles}
                    exclude={assignedIds}
                    title="Assign a vehicle"
                    planName={assignSub?.plan.name}
                    facilityName={assignSub?.facility.name}
                    onCreate={ v => addVehicle( v ) }
                    onSubmit={ v => { if( assignSub ) linkVehicle( assignSub.subscriptionId, v.vehicleId ); } }
                    onClose={() => setAssignSub( null )}
                />
                <ChooseSubscriptionModal
                    show={assignVehicle !== null}
                    setShow={ open => { if( !open ) setAssignVehicle( null ); } }
                    subscriptions={needing}
                    onSubmit={ s => { if( assignVehicle ) linkVehicle( s.subscriptionId, assignVehicle.vehicleId ); } }
                />
                <ConfirmModal
                    show={vehicleToDelete !== null}
                    setShow={ open => { if( !open ) setVehicleToDelete( null ); } }
                    title="Remove vehicle"
                    confirmText="Remove"
                    message={vehicleToDelete ? removeVehicleMessage( vehicleToDelete ) : ""}
                    onConfirm={confirmDelete}
                />
                <ConfirmModal
                    show={unlinkTarget !== null}
                    setShow={ open => { if( !open ) setUnlinkTarget( null ); } }
                    title="Unlink vehicle"
                    confirmText="Unlink"
                    message={unlinkTarget ? (
                        <>
                            <div>{unlinkVehicleMessage( unlinkTarget.vehicle, unlinkTarget.subscription )}</div>
                            <div className="alert alert-muted-gold d-flex align-items-start gap-2 mt-3 mb-0 small" role="alert">
                                <InfoCircleFill className="flex-shrink-0 mt-1" />
                                <span>This may affect enforcement and could lead to a citation if this vehicle is parked at the lot.</span>
                            </div>
                        </>
                    ) : ""}
                    onConfirm={confirmUnlink}
                />
                <PageTitle>
                    Vehicles
                </PageTitle>
                <div className="pb-1">
                    <div className="text-secondary">
                        Add or remove vehicles and see which subscription each one is on.
                    </div>
                </div>

                {(vehicles.length > 0 || !showFirstVehicleForm) && needCount > 0 && (
                    <div className="rounded-3 p-3 my-3" style={{ background: "#FFF6E8", border: "1px solid #F3E0C0" }}>
                        <div className="d-flex gap-2 mb-2">
                            <ExclamationTriangleFill className="mt-1 flex-shrink-0" style={{ color: "#E6A23C" }} />
                            <div>
                                <div className="fw-semibold">{needHeading}</div>
                                <div className="text-secondary small">{needDetail}</div>
                            </div>
                        </div>
                        {needing.map( sub =>
                            <div key={sub.subscriptionId}
                                 className="bg-white rounded-3 border d-flex align-items-center justify-content-between gap-2 px-3 py-2 mb-2">
                                <div className="min-w-0">
                                    <div className="fw-semibold">{sub.plan.name}</div>
                                    <div className="text-secondary small">{sub.facility.name}</div>
                                </div>
                                <Button size="sm" className="btn-main text-nowrap" onClick={() => setAssignSub( sub )}>
                                    Assign vehicle
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {vehicles.length === 0 ? (
                    showFirstVehicleForm ? (
                        <>
                            <AddVehicleInline
                                planName={needing[0]?.plan.name}
                                facilityName={needing[0]?.facility.name}
                                onSubmit={ v => addVehicle( v, needing[0]?.subscriptionId ) }
                                onDismiss={() => setShowFirstVehicleForm( false )}
                            />
                            {needing.slice( 1 ).map( sub =>
                                <div key={sub.subscriptionId}
                                     className="bg-white rounded-3 border d-flex align-items-center justify-content-between gap-2 px-3 py-2 mb-2">
                                    <div className="min-w-0">
                                        <div className="fw-semibold">{sub.plan.name}</div>
                                        <div className="text-secondary small">{sub.facility.name}</div>
                                    </div>
                                    <Button size="sm" className="btn-main text-nowrap" onClick={() => setAssignSub( sub )}>
                                        Assign vehicle
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : (
                    <div className="rounded-3 border bg-white text-center px-4 py-5 my-4 shadow-subtle">
                        <div
                            className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                            style={{
                                width: 72,
                                height: 72,
                                backgroundColor: "color-mix(in srgb, var(--color-mid) 12%, white)",
                            }}
                        >
                            <CarFront size={28} style={{ color: "var(--color-mid)" }} />
                        </div>
                        <p className="fw-semibold mb-2">You don&apos;t have any vehicles</p>
                        <p className="text-secondary small mb-4 mx-auto" style={{ maxWidth: 320 }}>
                            Add a vehicle to use it on parking plans and reservations.
                        </p>
                        <Button className="btn-main px-4" onClick={() => setShowFirstVehicleForm( true )}>
                            Add Vehicle
                        </Button>
                    </div>
                    )
                ) : (
                    <>
                        <div className="mt-3">
                        {vehicles.map( veh =>
                            <VehicleCard key={veh.vehicleId}
                                         vehicle={veh}
                                         deleteVehicle={deleteVehicle}
                                         vehicles={vehicles}
                                         subscriptions={veh.subscriptions ?? []}
                                         onUnlink={ subscription => setUnlinkTarget( { vehicle: veh, subscription } ) }
                                         onAssign={(veh.subscriptions?.length ?? 0) === 0 ? () => setAssignVehicle( veh ) : undefined} />
                        )}
                        </div>
                        <div className="text-end mt-3">
                            <Button className="w-100 btn-main  mb-3" onClick={() => setShowAddVehicle(true)}>+ Add Vehicle</Button>
                        </div>
                    </>
                )}
            </Col>
        </Row>
    </Container>;
}

function unlinkVehicleMessage( vehicle: Vehicle, subscription: VehicleSubscriptionSummary ) {
    const name = [vehicle.make, vehicle.model].filter( part => part && part !== "N/A" ).join( " " );
    const label = name ? `${name} (${vehicle.licensePlateNumber})` : vehicle.licensePlateNumber;
    return `Remove ${label} from ${subscription.planName}? The vehicle stays on your account.`;
}

function removeVehicleMessage( vehicle: Vehicle ) {
    const name = [vehicle.make, vehicle.model].filter( part => part && part !== "N/A" ).join( " " );
    const label = name ? `${name} (${vehicle.licensePlateNumber})` : vehicle.licensePlateNumber;
    const plan = vehicle.subscriptions?.[0];
    const planNote = plan
        ? ` It will also come off ${plan.planName}.`
        : "";
    return `Remove ${label} from your account?${planNote}`;
}

function plansNeedingVehicle( subs: SubListEntry[], vehicles: Vehicle[], myParkerId: number ): SubListEntry[] {
    const linked = new Set( vehicles.flatMap( v => (v.subscriptions ?? []).map( s => s.subscriptionId ) ) );
    return subs.filter( s =>
        s.status === SubscriptionStatus.Active
        && s.quantity > 0
        && !( s.parkerId === myParkerId && ( s.isGroup || s.isEnhancedGroup ) )
        && !linked.has( s.subscriptionId )
    );
}
